import * as React from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

export interface Modelo {
  id: number;
  descricao_modelo: string;
}

interface TransferListProps {
  origem: Modelo[];
  destino: Modelo[];
  destinoModelosExistentes: Modelo[];
  onChange: (selected: Modelo[]) => void;
}

function not(a: Modelo[], b: Modelo[]): Modelo[] {
  return a.filter((value) => !b.some((item) => item.id === value.id));
}

function intersection(a: Modelo[], b: Modelo[]): Modelo[] {
  return a.filter((value) => b.some((item) => item.id === value.id));
}

export default function TransferList({
  origem,
  destino,
  destinoModelosExistentes,
  onChange, 
}: TransferListProps) {
  const [checked, setChecked] = React.useState<Modelo[]>([]);
  const [left, setLeft] = React.useState<Modelo[]>(origem);
  const [right, setRight] = React.useState<Modelo[]>(destino);

  React.useEffect(() => {
    setLeft(origem);
    setRight(destino);
  }, [origem, destino]);

  React.useEffect(() => {
    onChange(right); 
  }, [right, onChange]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: Modelo) => () => {
    const currentIndex = checked.findIndex((item) => item.id === value.id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: Modelo[]) =>
    intersection(checked, items).length;

  const handleToggleAll = (items: Modelo[], disableAll: boolean) => () => {
    if (disableAll) return;
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked([...checked, ...not(items, checked)]);
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const customList = (
    title: React.ReactNode,
    items: Modelo[],
    disableItems: Modelo[] = []
  ) => {
    const disableAll = disableItems.length > 0;
    return (
      <Card>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={handleToggleAll(items, disableAll)}
              checked={
                numberOfChecked(items) === items.length && items.length !== 0
              }
              indeterminate={
                numberOfChecked(items) !== items.length &&
                numberOfChecked(items) !== 0
              }
              disabled={items.length === 0 || disableAll}
              inputProps={{ "aria-label": "all items selected" }}
            />
          }
          title={title}
          subheader={`${numberOfChecked(items)}/${items.length} selected`}
        />
        <Divider />
        <List
          sx={{
            width: "100%",
            height: 230,
            bgcolor: "background.paper",
            overflow: "auto",
          }}
          dense
          component="div"
          role="list"
        >
          {items.map((value) => {
            const labelId = `transfer-list-all-item-${value.id}-label`;
            const isDisabled = disableItems.some(
              (item) => item.id === value.id
            );

            return (
              <ListItemButton
                key={value.id}
                role="listitem"
                onClick={handleToggle(value)}
                disabled={isDisabled}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.some((item) => item.id === value.id)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                    disabled={isDisabled}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value.descricao_modelo} />
              </ListItemButton>
            );
          })}
        </List>
      </Card>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={{ width: "100%" }}
    >
      <Grid item xs={12} md={5}>
        {customList("Modelos Origem", left)}
      </Grid>
      <Grid item xs={12} md={2}>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12} md={5}>
        {customList("Modelos Destino", right, destinoModelosExistentes)}
      </Grid>
    </Grid>
  );
}
